/**
 * File Upload Integration Utilities
 * Helper functions to integrate file retention tracking with existing upload logic
 */

import { doc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { createFileBackupInfo, updateFileBackupInfo } from './fileRetentionUtils';
import SubscriptionService from '../services/SubscriptionService';

/**
 * Update file backup info after successful file upload
 * This function should be called after files are uploaded to Firebase Storage
 * @param {string} organizationId - Organization ID
 * @param {string} orderId - Order ID
 * @param {string} fileType - Type of file uploaded
 * @param {number} fileCount - Number of files uploaded
 * @returns {Promise<void>}
 */
export const updateFileBackupInfoAfterUpload = async (organizationId, orderId, fileType, fileCount = 1) => {
  try {
    // Get current order data
    const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      // Order not found - handled silently
      return;
    }

    const orderData = orderDoc.data();
    const currentBackupInfo = orderData.fileBackupInfo || {};
    
    // Get organization's current subscription plan
    const orgRef = doc(db, 'organizations', organizationId);
    const orgDoc = await getDoc(orgRef);
    const currentPlan = orgDoc.data()?.subscriptionPlan || 'basic';
    
    // Get upload date (use order creation date if no specific upload date)
    // FIX: Use actual Date/Timestamp, not serverTimestamp() sentinel
    let uploadDate;
    if (orderData.createdAt) {
      // Convert Firestore Timestamp to Date if needed
      uploadDate = orderData.createdAt?.toDate ? orderData.createdAt.toDate() : new Date(orderData.createdAt);
    } else {
      // Use current time if createdAt is not available
      uploadDate = new Date();
    }
    
    // Update file backup info
    const updatedBackupInfo = updateFileBackupInfo(
      currentBackupInfo,
      fileType,
      fileCount, // Positive number for adding files
      currentPlan,
      uploadDate
    );

    // Update the order document
    await updateDoc(orderRef, {
      fileBackupInfo: updatedBackupInfo,
      updatedAt: serverTimestamp()
    });

  } catch (error) {
    // Don't throw error to avoid breaking the upload process
  }
};

/**
 * Update file backup info after file removal
 * @param {string} organizationId - Organization ID
 * @param {string} orderId - Order ID
 * @param {string} fileType - Type of file removed
 * @param {number} fileCount - Number of files removed
 * @returns {Promise<void>}
 */
export const updateFileBackupInfoAfterRemoval = async (organizationId, orderId, fileType, fileCount = 1) => {
  try {
    // Get current order data
    const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      // Order not found - handled silently
      return;
    }

    const orderData = orderDoc.data();
    const currentBackupInfo = orderData.fileBackupInfo || {};
    
    // Get organization's current subscription plan
    const orgRef = doc(db, 'organizations', organizationId);
    const orgDoc = await getDoc(orgRef);
    const currentPlan = orgDoc.data()?.subscriptionPlan || 'basic';
    
    // Get upload date (use order creation date if no specific upload date)
    // FIX: Use actual Date/Timestamp, not serverTimestamp() sentinel
    let uploadDate;
    if (orderData.createdAt) {
      // Convert Firestore Timestamp to Date if needed
      uploadDate = orderData.createdAt?.toDate ? orderData.createdAt.toDate() : new Date(orderData.createdAt);
    } else {
      // Use current time if createdAt is not available
      uploadDate = new Date();
    }
    
    // Update file backup info (negative number for removing files)
    const updatedBackupInfo = updateFileBackupInfo(
      currentBackupInfo,
      fileType,
      -fileCount, // Negative number for removing files
      currentPlan,
      uploadDate
    );

    // Update the order document
    await updateDoc(orderRef, {
      fileBackupInfo: updatedBackupInfo,
      updatedAt: serverTimestamp()
    });

  } catch (error) {
    // Don't throw error to avoid breaking the removal process
  }
};

/**
 * Initialize file backup info for a new order
 * This should be called when creating a new order
 * @param {string} organizationId - Organization ID
 * @param {string} orderId - Order ID
 * @param {string} planId - Subscription plan ID
 * @returns {Promise<void>}
 */
export const initializeFileBackupInfo = async (organizationId, orderId, planId) => {
  try {
    const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
    // FIX: Use actual Date, not serverTimestamp() sentinel
    const uploadDate = new Date();
    
    // Create initial file backup info
    const fileBackupInfo = createFileBackupInfo(planId, uploadDate, {});
    
    // Update the order document
    await updateDoc(orderRef, {
      fileBackupInfo,
      updatedAt: serverTimestamp()
    });

  } catch (error) {
    // Don't throw error to avoid breaking the order creation process
  }
};

/**
 * Get file backup status for display in UI
 * @param {string} organizationId - Organization ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object|null>} File backup status or null if not found
 */
export const getFileBackupStatus = async (organizationId, orderId) => {
  try {
    const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      return null;
    }

    const orderData = orderDoc.data();
    const fileBackupInfo = orderData.fileBackupInfo;
    
    if (!fileBackupInfo) {
      return null;
    }

    // Get organization's current subscription plan
    const orgRef = doc(db, 'organizations', organizationId);
    const orgDoc = await getDoc(orgRef);
    const currentPlan = orgDoc.data()?.subscriptionPlan || 'basic';
    
    return {
      orderId,
      fileBackupInfo,
      planId: currentPlan,
      backupPeriod: SubscriptionService.getBackupPeriod(currentPlan)
    };

  } catch (error) {
    return null;
  }
};
